import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { CreateOrderDTO, ShippingProfileDTO } from '@invenco/sales-interface/interfaces';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
} from '@/components/core/combobox';
import { Form } from '@/components/core/form';
import { ComboboxFormField } from '@/components/forms/combobox-form-field';
import { NumberFormField } from '@/components/forms/number-form-field';
import { WizardStep } from '@/components/wizard';
import type { WizardStepDefinition } from '@/components/wizard/wizard-context';
import { useGateways } from '@/gateways/GatewayProvider';
import { useHasFormValues } from '@/shared/hooks/use-has-form-values';
import { useIsFormValid } from '@/shared/hooks/use-is-form-valid';

const shippingSchema = z.object({
  shippingProfileName: z.string().min(1, 'Select a shipping profile'),
  subtotalShipping: z
    .number('Enter a shipping charge')
    .nonnegative('Enter a valid shipping charge'),
});

const valueFields = ['shippingProfileName', 'subtotalShipping'] as const;

type ShippingFormValues = z.output<typeof shippingSchema>;

export function ShippingStep({
  step,
  channelName,
  onChange,
}: {
  step: WizardStepDefinition;
  channelName: string;
  onChange: (input: Partial<CreateOrderDTO>) => void;
}) {
  const { salesGateway } = useGateways();
  const form = useForm<ShippingFormValues>({
    resolver: zodResolver(shippingSchema),
    defaultValues: { shippingProfileName: '', subtotalShipping: undefined },
  });
  const isValid = useIsFormValid(form, shippingSchema);
  const hasValues = useHasFormValues(form, valueFields);

  const { data: channelData } = useQuery({
    queryKey: ['channels'],
    queryFn: ({ signal }) => salesGateway.getChannels(undefined, { signal }),
  });
  const { data: shippingProfileData } = useQuery({
    queryKey: ['shippingProfiles'],
    queryFn: ({ signal }) => salesGateway.getShippingProfiles(undefined, { signal }),
  });

  const selectedChannelId = useMemo(
    () => channelData?.items.find((channel) => channel.name === channelName)?.id,
    [channelData?.items, channelName],
  );
  const shippingProfileOptions = useMemo<ShippingProfileDTO[]>(() => {
    const profiles = shippingProfileData?.items ?? [];
    return selectedChannelId
      ? profiles.filter((profile) => profile.channelId === selectedChannelId || !profile.channelId)
      : profiles;
  }, [selectedChannelId, shippingProfileData?.items]);

  const submitSection = async () => {
    let valid = false;

    await form.handleSubmit((data) => {
      onChange({
        shippingProfileName: data.shippingProfileName,
        subtotalShipping: data.subtotalShipping,
      });
      valid = true;
    })();

    return valid;
  };

  return (
    <WizardStep step={step} complete={isValid && hasValues} onSubmitStep={submitSection}>
      <Card aria-label="Shipping">
        <CardHeader>
          <CardTitle>Shipping</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form className="grid gap-4">
              <ComboboxFormField
                control={form.control}
                name="shippingProfileName"
                label="Shipping profile"
                items={shippingProfileOptions}
                aria-label="Shipping profile"
              >
                <ComboboxContent>
                  <ComboboxEmpty>No shipping profiles found</ComboboxEmpty>
                  <ComboboxList>
                    {(profile: ShippingProfileDTO) => (
                      <ComboboxItem key={profile.id} value={profile.name}>
                        {profile.name}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </ComboboxFormField>
              <NumberFormField
                control={form.control}
                name="subtotalShipping"
                label="Shipping charge"
                aria-label="Shipping charge"
                min={0}
                step="any"
              />
            </form>
          </Form>
        </CardContent>
      </Card>
    </WizardStep>
  );
}
